import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/config/axiosConfig";
import { toast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BackToTopButton from "@/components/common/BackToTopButton";

// Background photo component
const BackgroundPhoto = ({
  top,
  left,
  width,
  height,
  delay,
  src,
}: {
  top: number;
  left: number;
  width: string;
  height: string;
  delay: number;
  src: string;
}) => (
  <div
    className="absolute rounded-xl overflow-hidden"
    style={{
      top: `${top}%`,
      left: `${left}%`,
      width: width,
      height: height,
      animation: `float 20s ease-in-out ${delay}s infinite`,
      zIndex: 1,
    }}
  >
    <img
      src={src}
      alt=""
      loading="eager"
      className="w-full h-full object-cover opacity-70 blur-xs"
      style={{ transform: "scale(1.1)" }}
    />
  </div>
);

const CopyrightRemovalPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    copyrightOwnership: "owner",
    contentDescription: "",
    copyrightProof: "",
    contentLocation: "",
    signature: "",
    contactInfo: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [displayPhotos, setDisplayPhotos] = useState<Array<any>>([]);
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Background photos
  const photos = [
    {
      width: "450px",
      height: "350px",
      top: 15,
      left: 10,
      delay: 0,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG5.jpg",
    },
    {
      width: "400px",
      height: "300px",
      top: 15,
      left: 60,
      delay: 1,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG1.jpg",
    },
    {
      width: "420px",
      height: "320px",
      top: 60,
      left: 10,
      delay: 2,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG2.jpg",
    },
    {
      width: "430px",
      height: "330px",
      top: 60,
      left: 60,
      delay: 2.5,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG3.jpg",
    },
  ];

  // Listen for window resize and update state
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    updatePhotos();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update photos when window width changes
  useEffect(() => {
    updatePhotos();
  }, [windowWidth]);

  // Responsive photo config
  const updatePhotos = () => {
    if (windowWidth < 640) {
      setDisplayPhotos(photos.slice(0, 1));
    } else if (windowWidth < 1024) {
      setDisplayPhotos(photos.slice(0, 2));
    } else {
      setDisplayPhotos(photos);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // This endpoint is mocked for now
      await api.content.post("/copyright/removal", formData);

      toast({
        title: "Request submitted successfully",
        description: "We will review your request and respond accordingly.",
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        copyrightOwnership: "owner",
        contentDescription: "",
        copyrightProof: "",
        contentLocation: "",
        signature: "",
        contactInfo: "",
      });
    } catch (error) {
      toast({
        title: "Error submitting request",
        description: "Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="relative no-scrollbar w-full min-h-screen flex flex-col bg-black text-white overflow-hidden">
      {/* Animation style */}
      <style>
        {`
        @keyframes float {
          0% { transform: translate(0, 0) rotate(0deg); }
          25% { transform: translate(-10px, 10px) rotate(1deg); }
          50% { transform: translate(0, 20px) rotate(0deg); }
          75% { transform: translate(10px, 10px) rotate(-1deg); }
          100% { transform: translate(0, 0) rotate(0deg); }
        }
        `}
      </style>

      {/* Background Photos Layer */}
      <div className="absolute inset-0 overflow-hidden">
        {displayPhotos.map((photo, index) => (
          <BackgroundPhoto
            key={index}
            width={windowWidth < 640 ? "100%" : photo.width}
            height={windowWidth < 640 ? "100%" : photo.height}
            top={windowWidth < 640 ? 0 : photo.top}
            left={windowWidth < 640 ? 0 : photo.left}
            delay={photo.delay}
            src={photo.src}
          />
        ))}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-[2]"></div>
      </div>

      {/* Header */}
      <Header showHomeButton={true} />

      {/* Main Content */}
      <main className="flex-grow flex items-center justify-center px-4 py-6 sm:py-8 overflow-y-auto">
        <div className="w-full max-w-2xl p-6 sm:p-8 rounded-xl border border-white/20 bg-black/40 backdrop-blur-md shadow-2xl z-10 my-8">
          <h1 className="text-2xl sm:text-3xl font-light mb-2 text-center text-white/90">
            Copyright Material Removal Request
          </h1>

          <p className="text-sm sm:text-base text-gray-300 mb-6 text-center">
            Please complete this form to submit a copyright infringement notice.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm text-gray-300 mb-2 font-light"
                >
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-black/60 border border-white/30 rounded-lg 
                  text-sm sm:text-base font-light placeholder-gray-500 focus:outline-none focus:border-indigo-500
                  transition-all duration-300"
                  placeholder="Your full name"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm text-gray-300 mb-2 font-light"
                >
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-black/60 border border-white/30 rounded-lg 
                  text-sm sm:text-base font-light placeholder-gray-500 focus:outline-none focus:border-indigo-500
                  transition-all duration-300"
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="copyrightOwnership"
                className="block text-sm text-gray-300 mb-2 font-light"
              >
                Copyright Ownership Status
              </label>
              <select
                id="copyrightOwnership"
                name="copyrightOwnership"
                value={formData.copyrightOwnership}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-black/60 border border-white/30 rounded-lg 
                text-sm sm:text-base font-light text-gray-300 focus:outline-none focus:border-indigo-500
                transition-all duration-300"
                required
              >
                <option value="owner">I am the copyright owner</option>
                <option value="authorized">
                  I am authorized to act on behalf of the copyright owner
                </option>
                <option value="neither">
                  I am neither the owner nor authorized
                </option>
              </select>
            </div>

            <div>
              <label
                htmlFor="contentDescription"
                className="block text-sm text-gray-300 mb-2 font-light"
              >
                Description of Copyrighted Work
              </label>
              <textarea
                id="contentDescription"
                name="contentDescription"
                value={formData.contentDescription}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-3 bg-black/60 border border-white/30 rounded-lg 
                text-sm sm:text-base font-light placeholder-gray-500 focus:outline-none focus:border-indigo-500 resize-none
                transition-all duration-300"
                placeholder="Please describe the copyrighted work in detail"
                required
              />
            </div>

            <div>
              <label
                htmlFor="contentLocation"
                className="block text-sm text-gray-300 mb-2 font-light"
              >
                Location of Infringing Content
              </label>
              <textarea
                id="contentLocation"
                name="contentLocation"
                value={formData.contentLocation}
                onChange={handleChange}
                rows={2}
                className="w-full px-4 py-3 bg-black/60 border border-white/30 rounded-lg 
                text-sm sm:text-base font-light placeholder-gray-500 focus:outline-none focus:border-indigo-500 resize-none
                transition-all duration-300"
                placeholder="Please provide the URL(s) where the infringing content appears"
                required
              />
            </div>

            <div>
              <label
                htmlFor="copyrightProof"
                className="block text-sm text-gray-300 mb-2 font-light"
              >
                Proof of Copyright (Optional)
              </label>
              <textarea
                id="copyrightProof"
                name="copyrightProof"
                value={formData.copyrightProof}
                onChange={handleChange}
                rows={2}
                className="w-full px-4 py-3 bg-black/60 border border-white/30 rounded-lg 
                text-sm sm:text-base font-light placeholder-gray-500 focus:outline-none focus:border-indigo-500 resize-none
                transition-all duration-300"
                placeholder="Provide links to original work or registration information"
              />
            </div>

            <div>
              <label
                htmlFor="contactInfo"
                className="block text-sm text-gray-300 mb-2 font-light"
              >
                Additional Contact Information
              </label>
              <textarea
                id="contactInfo"
                name="contactInfo"
                value={formData.contactInfo}
                onChange={handleChange}
                rows={2}
                className="w-full px-4 py-3 bg-black/60 border border-white/30 rounded-lg 
                text-sm sm:text-base font-light placeholder-gray-500 focus:outline-none focus:border-indigo-500 resize-none
                transition-all duration-300"
                placeholder="Phone number, address, etc."
              />
            </div>

            <div>
              <label
                htmlFor="signature"
                className="block text-sm text-gray-300 mb-2 font-light"
              >
                Electronic Signature
              </label>
              <input
                type="text"
                id="signature"
                name="signature"
                value={formData.signature}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-black/60 border border-white/30 rounded-lg 
                text-sm sm:text-base font-light placeholder-gray-500 focus:outline-none focus:border-indigo-500
                transition-all duration-300"
                placeholder="Type your full name as signature"
                required
              />
            </div>

            <div className="pt-3">
              <p className="text-xs text-gray-400 mb-4">
                By submitting this request, I affirm that:
                <br />
                1. I have a good faith belief that the use of the material in
                the manner complained of is not authorized by the copyright
                owner, its agent, or the law.
                <br />
                2. The information in this notification is accurate, and under
                penalty of perjury, I am the owner of an exclusive right that is
                allegedly infringed, or I am authorized to act on behalf of the
                owner.
              </p>

              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base text-white border border-white/30 rounded-lg
                  hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed
                  transition-all duration-300 relative group backdrop-blur-sm w-full sm:w-auto"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      <span>Submitting...</span>
                    </div>
                  ) : (
                    <span className="relative z-10">Submit Request</span>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </main>

      {/* Footer */}
      <Footer />

      <BackToTopButton />
    </div>
  );
};

export default CopyrightRemovalPage;
